import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tree } from 'react-organizational-chart';
import './styles.css';
import OrgTreeNode from './node';
import { NodeCard } from './node';
import DepartmentForm from './form';
import DepartmentFormView from './DepartmentFormView';
import CommonModal from '../CommonModal/CommonModal';
import Button from 'apollo-react/components/Button';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  searchForNode,
  resetSearchingForNode,
  getFoundNode,
  attachChildToParent,
  createNewNode,
  arraysEqual,
  updateChildrenHead,
  fetchUsersAPI,
  fetchOrganizationAPI,
  validateParentForChild,
  saveDepartmentAPI,
  deleteDepartmentAPI,
  validateNodeData,
  maxDepartmentIdInTree,
  maxDepartmentLevelInTree,
} from './utils/treeUtils';
import { openModal, closeModal } from './utils/modalUtils';
import { useNavigate, useParams } from 'react-router-dom';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';

import { EXTERNAL_MY_ORG_PRIVILEGES } from '../../../constants/privilege.constant';
import { RESOURCES } from '../../../constants/resourceConstants';

// Default root node structure
const headNode = {
  id: 1,
  name: 'Head',
  code: 'HM-001',
  level: 1,
  head: '',
  manager: '',
  children: [],
};

/**
 * Main Organizational Tree Component
 * Manages the entire organizational chart structure with CRUD operations
 */
function OrgTree({ userPrivileges = {} }) {
  // ========== STATE MANAGEMENT ==========
  const { t } = useTranslation();
  const [tree, setTree] = useState(headNode);
  const [users, setUsers] = useState([]);
  const [nodesCount, setNodesCount] = useState(1);
  const [lastLevel, setLastLevel] = useState(1);
  const [organizationType, setOrganizationType] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [modalState, setModalState] = useState({
    editModal: false,
    viewModal: false,
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Global state to prevent user assignment conflicts across nodes
  const [globalSelectedManagers, setGlobalSelectedManagers] = useState([]);
  const [globalSelectedEmployees, setGlobalSelectedEmployees] = useState([]);
  const [globalSelectedHeads, setGlobalSelectedHeads] = useState([]);
  const [toolbarSaving, setToolbarSaving] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const { privileges, isExternalUser } = userPrivileges;
  const disableInviteUsers = isExternalUser
    ? privileges.find((priv) => {
        return (
          priv.name === EXTERNAL_MY_ORG_PRIVILEGES.INVITE_MY_ORG_USERS &&
          priv.Resource.name === RESOURCES.EXTERNAL_MY_ORG_PRIVILEGES
        );
      })
      ? false
      : true
    : false;

  const showViewButtonInNode = isExternalUser
    ? privileges.find((priv) => {
        return (
          priv.name === EXTERNAL_MY_ORG_PRIVILEGES.VIEW_MY_ORG &&
          priv.Resource.name === RESOURCES.EXTERNAL_MY_ORG_PRIVILEGES
        );
      })
      ? true
      : false
    : false;

  const showEditButtonInNode = isExternalUser
    ? privileges.find((priv) => {
        return (
          priv.name === EXTERNAL_MY_ORG_PRIVILEGES.EDIT_MY_ORG_DEPARTMENTS &&
          priv.Resource.name === RESOURCES.EXTERNAL_MY_ORG_PRIVILEGES
        );
      })
      ? true
      : false
    : true;

  const showAddChildButtonInNode = isExternalUser
    ? privileges.find((priv) => {
        return (
          priv.name === EXTERNAL_MY_ORG_PRIVILEGES.ADD_MY_ORG_DEPARTMENTS &&
          priv.Resource.name === RESOURCES.EXTERNAL_MY_ORG_PRIVILEGES
        );
      })
      ? true
      : false
    : true;

  const showDeleteButtonInNode = isExternalUser
    ? privileges.find((priv) => {
        return (
          priv.name === EXTERNAL_MY_ORG_PRIVILEGES.ADD_MY_ORG_DEPARTMENTS &&
          priv.Resource.name === RESOURCES.EXTERNAL_MY_ORG_PRIVILEGES
        );
      })
      ? true
      : false
    : true;

  const hasUnsavedStructure = useMemo(() => {
    const walk = (n) => {
      if (n.isNew) return true;
      return (n.children || []).some(walk);
    };
    return walk(tree);
  }, [tree]);

  /**
   * Fetches all active users from the API using utility function
   */
  const fetchUsers = useCallback(async () => {
    try {
      const activeUsers = await fetchUsersAPI(
        organizationType,
        organizationType === 'external' ? id : null,
        organizationType === 'external' ? categoryId : null,
        id
      );

      setUsers(activeUsers);
    } catch (error) {
      // Error handling is done in the utility function
    }
  }, [organizationType, categoryId, id]);

  /**
   * Fetches organization structure using utility function and updates state
   */
  const fetchOrganization = useCallback(async () => {
    try {
      setGlobalSelectedEmployees([]);
      setGlobalSelectedManagers([]);
      setGlobalSelectedHeads([]);
      const result = await fetchOrganizationAPI(
        id,
        setGlobalSelectedEmployees,
        setGlobalSelectedManagers,
        setGlobalSelectedHeads
      );

      if (result) {
        setTree(result.rootNode);
        setNodesCount(result.maxId);
        setLastLevel(result.maxLevel);
        setOrganizationType(result.organizationType);
        setCategoryId(result.categoryId);
      }
    } catch (error) {
      // Error handling is done in the utility function
    }
  }, [id]);

  // ========== DATA FETCHING ==========
  useEffect(() => {
    const initializeData = async () => {
      if (id) {
        await fetchOrganization();
      } else {
        await fetchUsers();
      }
    };
    initializeData();
    // Run only when route org id changes. Listing fetchOrganization/fetchUsers would re-run
    // full structure load when their identities change (e.g. after organizationType is set).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount + id transition only
  }, [id]);

  useEffect(() => {
    if (id && organizationType) {
      fetchUsers();
    }
  }, [organizationType, categoryId, id, fetchUsers]);

  const saveAllNewDepartments = useCallback(async () => {
    const treeCopy = structuredClone(tree);
    const pending = [];
    const collectPreorder = (n) => {
      if (n.isNew) pending.push(n);
      (n.children || []).forEach(collectPreorder);
    };
    collectPreorder(treeCopy);

    if (pending.length === 0) {
      return true;
    }

    for (const node of pending) {
      const validation = validateNodeData(node);
      if (!validation.isValid) {
        toast.error(t('complete_each_new_department'));
        return false;
      }
    }

    for (const node of pending) {
      try {
        // eslint-disable-next-line no-await-in-loop -- parent rows must exist in DB before children
        const departmentId = await saveDepartmentAPI(node, treeCopy, id);
        if (node.isNew) {
          node.id = departmentId;
          delete node.isNew;
          node.children.forEach((child) => {
            child.parent_id = node.id;
          });
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.error?.message ||
          t('failed_to_save_department');
        toast.error(errorMessage, {
          style: { whiteSpace: 'pre-line' },
        });
        // Resync from server: some departments may have been created before the failure.
        // eslint-disable-next-line no-await-in-loop -- recovery refetch; loop exits on return
        await fetchOrganization();
        // eslint-disable-next-line no-await-in-loop
        await fetchUsers();
        return false;
      }
    }

    setTree(treeCopy);
    setNodesCount(maxDepartmentIdInTree(treeCopy));
    setLastLevel(maxDepartmentLevelInTree(treeCopy));
    await fetchUsers();
    return true;
  }, [tree, id, t, fetchUsers, fetchOrganization]);

  const saveStructureWithSuccessToast = useCallback(async () => {
    const ok = await saveAllNewDepartments();
    if (ok) {
      toast.success(t('org_structure_save_complete'));
    }
    return ok;
  }, [saveAllNewDepartments, t]);

  const handleToolbarDiscard = useCallback(async () => {
    if (toolbarSaving) return;
    setToolbarSaving(true);
    try {
      if (hasUnsavedStructure) {
        await fetchOrganization();
        await fetchUsers();
        toast.success(t('org_structure_changes_discarded'));
      } else {
        toast.success(t('org_structure_no_unsaved_to_discard'));
        navigate(-1);
      }
    } finally {
      setToolbarSaving(false);
    }
  }, [
    toolbarSaving,
    hasUnsavedStructure,
    fetchOrganization,
    fetchUsers,
    t,
    navigate,
  ]);

  const handleToolbarSave = useCallback(async () => {
    if (toolbarSaving) return;
    setToolbarSaving(true);
    try {
      const ok = await saveStructureWithSuccessToast();
      if (ok) {
        navigate(-1);
      }
    } finally {
      setToolbarSaving(false);
    }
  }, [toolbarSaving, saveStructureWithSuccessToast, navigate]);

  useUnsavedChangesWarning(hasUnsavedStructure);

  // ========== MODAL MANAGEMENT ==========
  /**
   * Opens a modal for editing a node
   */
  const handleModalOpen = (variant, node = null) => {
    openModal(modalState, setModalState, variant);
    if (node) setSelectedNode(node);
  };

  /**
   * Closes the modal and clears selected node
   */
  const handleModalClose = (variant) => {
    closeModal(modalState, setModalState, variant);
    setSelectedNode(null);
  };

  // ========== TREE RENDERING ==========
  /**
   * Recursively renders tree nodes
   */
  const renderNodes = (node) => {
    return (
      <OrgTreeNode
        key={node.id}
        node={node}
        addChild={addChild}
        editNode={editNodeData}
        onEditClick={handleModalOpen}
        onDeleteClick={handleDeleteNode}
        users={users}
        showViewButtonInNode={showViewButtonInNode}
        showEditButtonInNode={showEditButtonInNode}
        showAddChildButtonInNode={showAddChildButtonInNode}
        showDeleteButtonInNode={showDeleteButtonInNode}
      >
        {node.children.map((child) => renderNodes(child))}
      </OrgTreeNode>
    );
  };

  // ========== TREE OPERATIONS ==========
  /**
   * Adds a new child node to the specified parent
   */
  const addChild = (parentNode) => {
    // Validate parent node using utility function
    const validation = validateParentForChild(parentNode);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    const newNode = createNewNode(nodesCount, parentNode);
    const treeCopy = structuredClone(tree);

    // Find parent and attach new child
    searchForNode(treeCopy, parentNode.id);
    attachChildToParent(getFoundNode(), newNode);

    // Update tree state and counters
    setTree(treeCopy);
    setNodesCount(nodesCount + 1);

    if (newNode.level > lastLevel) {
      setLastLevel(newNode.level);
    }

    resetSearchingForNode();
  };

  /**
   * Updates node data and handles head hierarchy changes
   */
  const editNodeData = async (node, data) => {
    const treeCopy = structuredClone(tree);
    const level = node.level;

    // Find and update the target node
    searchForNode(treeCopy, node.id);
    const foundNode = getFoundNode();

    // Store old head for comparison
    const oldHead = level === 1 ? foundNode.head : null;

    // Update node properties
    foundNode.name = data.name;
    foundNode.description = data.description;
    foundNode.manager = data.manager;
    foundNode.employees = data.employees;
    foundNode.head = data.head;

    // Update invited user fields
    foundNode.invitedHead = data.invitedHead;
    foundNode.invitedManager = data.invitedManager;
    foundNode.invitedEmployees = data.invitedEmployees;

    // Handle head changes and cascade to children
    if (level === 1 && !arraysEqual(oldHead, foundNode.head)) {
      updateChildrenHead(foundNode, data.head);
    } else if (level !== 1) {
      // For non-root nodes, manager becomes head for children
      updateChildrenHead(foundNode, data.manager);
    }

    try {
      const departmentId = await saveDepartmentAPI(foundNode, treeCopy, id);

      if (foundNode.isNew) {
        foundNode.id = departmentId;
        delete foundNode.isNew;
        foundNode.children.forEach((child) => {
          child.parent_id = foundNode.id;
        });
      }

      setTree(treeCopy);
      toast.success(t('department_saved_successfully'));
    } catch (error) {
      console.error('Failed to save department:', error);
      throw error;
    } finally {
      resetSearchingForNode();
      handleModalClose('editModal');
    }
  };

  /**
   * deletes a child department node after user confirmation
   */
  const handleDeleteNode = (node) => {
    if (node.level === 1) {
      toast.error(t('cannot_delete_root_department'));
      return;
    }
    if (node.children?.length > 0) {
      toast.error(t('cannot_delete_department_with_children'));
      return;
    }
    setDeleteTarget(node);
  };

  const confirmDelete = async () => {
    const node = deleteTarget;
    setDeleteTarget(null);
    if (!node) return;

    try {
      await deleteDepartmentAPI(id, node.id);
      await fetchOrganization();
      await fetchUsers();
      toast.success(t('department_deleted_successfully'));
    } catch (error) {
      // error toast is handled in deleteDepartmentAPI
    }
  };

  /**
   * Updates head assignment for all children of a node
   * Used for cascading head changes down the hierarchy
   */
  const setChildrenHead = (node, head) => {
    const treeCopy = structuredClone(tree);
    searchForNode(treeCopy, node.id);
    updateChildrenHead(getFoundNode(), head);
    setTree(treeCopy);
    resetSearchingForNode();
  };

  // ========== RENDER ==========
  return (
    <div className="org-tree-container">
      <div className="org-tree-scroll">
        <Tree
          label={
            <NodeCard
              node={tree}
              addChild={addChild}
              editNode={editNodeData}
              onEditClick={handleModalOpen}
              users={users}
              showViewButtonInNode={showViewButtonInNode}
              showEditButtonInNode={showEditButtonInNode}
              showAddChildButtonInNode={showAddChildButtonInNode}
              showDeleteButtonInNode={showDeleteButtonInNode}
            />
          }
        >
          {tree.children.map((child) => renderNodes(child))}
        </Tree>
      </div>

      <div className="org-structure-toolbar">
        <Button
          variant="outlined"
          color="secondary"
          disabled={toolbarSaving}
          onClick={handleToolbarDiscard}
        >
          {t('discard')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={toolbarSaving}
          onClick={handleToolbarSave}
        >
          {toolbarSaving ? t('Loading...') : t('save')}
        </Button>
      </div>

      {/* Department editing modal */}
      {modalState.editModal && selectedNode && (
        <div className="modal-overlay">
          <div className="modal-content-wrapper">
            <DepartmentForm
              node={selectedNode}
              editNode={editNodeData}
              onCancel={() => handleModalClose('editModal')}
              globalSelectedManagers={globalSelectedManagers}
              setGlobalSelectedManagers={setGlobalSelectedManagers}
              globalSelectedEmployees={globalSelectedEmployees}
              setGlobalSelectedEmployees={setGlobalSelectedEmployees}
              setGlobalSelectedHeads={setGlobalSelectedHeads}
              globalSelectedHeads={globalSelectedHeads}
              setChildrenHead={setChildrenHead}
              users={users}
              isModal={true}
              disableInviteUsers={disableInviteUsers}
            />
          </div>
        </div>
      )}

      {modalState.viewModal && selectedNode && (
        <div className="modal-overlay">
          <div className="modal-content-wrapper">
            <DepartmentFormView
              node={selectedNode}
              onCancel={() => handleModalClose('viewModal')}
              users={users}
              isModal={true}
            />
          </div>
        </div>
      )}

      {/* Department delete confirmation modal */}
      <CommonModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title={t('delete_department')}
        message={t('confirm_delete_department', { name: deleteTarget?.name })}
        size="small"
        variant="warning"
        buttonProps={[
          {
            label: t('cancel'),
            onClick: () => setDeleteTarget(null),
          },
          {
            label: t('delete'),
            onClick: confirmDelete,
          },
        ]}
      />
    </div>
  );
}

export default OrgTree;
